import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import logger from '../utils/logger';

/**
 * Dashboard Service
 * Fetches aggregated data for the Family Board Dashboard
 */

/**
 * Get recent announcements
 * @param {string} familyCode - Family code
 * @param {number} limitCount - Number of announcements to fetch
 * @returns {Promise<Array>} Array of announcements
 */
export const getRecentAnnouncements = async (familyCode, limitCount = 5) => {
  try {
    const announcementsQuery = query(
      collection(db, 'families', familyCode, 'announcements'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(announcementsQuery);
    const announcements = [];

    querySnapshot.forEach(doc => {
      announcements.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return announcements;
  } catch (error) {
    logger.error('Error fetching recent announcements:', error);
    return [];
  }
};

/**
 * Get upcoming events
 * @param {string} familyCode - Family code
 * @param {number} limitCount - Number of events to fetch
 * @returns {Promise<Array>} Array of upcoming events
 */
export const getUpcomingEvents = async (familyCode, limitCount = 5) => {
  try {
    // Events store date as a string in "YYYY-MM-DD" format from the HTML date input.
    // Compare with today as a string so Firestore can match types.
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0]; // e.g. "2026-03-03"

    const eventsQuery = query(
      collection(db, 'families', familyCode, 'events'),
      where('date', '>=', todayStr),
      orderBy('date', 'asc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(eventsQuery);
    const events = [];

    querySnapshot.forEach(doc => {
      events.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return events;
  } catch (error) {
    logger.error('Error fetching upcoming events:', error);
    return [];
  }
};

/**
 * Get recent photos from Family Documents
 * Family photos are stored in the familyDocuments collection with storageType='photos'
 * Also includes legacy image files that don't have storageType set (backwards compatibility)
 * @param {string} familyCode - Family code
 * @param {number} limitCount - Number of photos to fetch
 * @returns {Promise<Array>} Array of recent photos
 */
export const getRecentPhotos = async (familyCode, limitCount = 6) => {
  logger.log('[getRecentPhotos] Fetching photos for family:', familyCode);
  // Helper to check if file is an image (not a folder)
  const isImageFile = (data) => {
    if (data.type === 'folder') return false;
    if (data.fileType && data.fileType.startsWith('image/')) return true;
    const fileName = data.fileName || data.name || '';
    const ext = fileName.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext);
  };

  // Helper to extract photo with URL
  const toPhoto = (doc) => {
    const data = doc.data();
    const url = data.downloadURL || data.url || data.photoURL;
    if (!url || data.type === 'folder') return null;
    return { id: doc.id, ...data, url };
  };

  try {
    // First, try to query with storageType='photos' (requires composite index)
    const photosQuery = query(
      collection(db, 'families', familyCode, 'familyDocuments'),
      where('storageType', '==', 'photos'),
      orderBy('uploadedAt', 'desc'),
      limit(limitCount + 5) // fetch extra to account for folders
    );

    const querySnapshot = await getDocs(photosQuery);
    const photos = [];

    querySnapshot.forEach(doc => {
      const photo = toPhoto(doc);
      if (photo) photos.push(photo);
    });

    logger.log('[getRecentPhotos] Primary query returned', querySnapshot.size, 'docs,', photos.length, 'valid photos');

    if (photos.length > 0) {
      return photos.slice(0, limitCount);
    }
  } catch (error) {
    // Index might not exist yet - fall through to fallback queries
    logger.warn('[getRecentPhotos] Primary query failed:', error.message);
    logger.warn('storageType query failed (index may not exist), falling back:', error.message);
  }

  // Fallback 1: query all documents ordered by uploadedAt and filter for images
  try {
    const allDocsQuery = query(
      collection(db, 'families', familyCode, 'familyDocuments'),
      orderBy('uploadedAt', 'desc'),
      limit(limitCount * 5)
    );

    const allDocsSnapshot = await getDocs(allDocsQuery);
    const legacyPhotos = [];

    allDocsSnapshot.forEach(doc => {
      const data = doc.data();
      if (isImageFile(data)) {
        const url = data.downloadURL || data.url || data.photoURL;
        if (url) {
          legacyPhotos.push({ id: doc.id, ...data, url });
        }
      }
    });

    logger.log('[getRecentPhotos] Fallback 1 found', legacyPhotos.length, 'image files from', allDocsSnapshot.size, 'total docs');

    if (legacyPhotos.length > 0) {
      return legacyPhotos.slice(0, limitCount);
    }
  } catch (error) {
    logger.warn('[getRecentPhotos] Fallback 1 failed:', error.message);
    logger.warn('Ordered fallback query failed, trying unordered:', error.message);
  }

  // Fallback 2: no ordering — just get all docs and filter + sort in memory
  // This requires no indexes at all
  try {
    const collRef = collection(db, 'families', familyCode, 'familyDocuments');
    const allDocsSnapshot = await getDocs(collRef);
    const photos = [];

    allDocsSnapshot.forEach(doc => {
      const data = doc.data();
      if (data.type === 'folder') return;
      const url = data.downloadURL || data.url || data.photoURL;
      if (!url) return;
      if (isImageFile(data) || data.storageType === 'photos') {
        photos.push({ id: doc.id, ...data, url });
      }
    });

    // Sort by uploadedAt descending in memory
    photos.sort((a, b) => {
      const aTime = a.uploadedAt?.toMillis?.() || a.uploadedAt?.seconds * 1000 || 0;
      const bTime = b.uploadedAt?.toMillis?.() || b.uploadedAt?.seconds * 1000 || 0;
      return bTime - aTime;
    });

    return photos.slice(0, limitCount);
  } catch (error) {
    logger.error('Error fetching recent photos:', error);
    return [];
  }
};

/**
 * Get recent recommendations
 * @param {string} familyCode - Family code
 * @param {number} limitCount - Number of recommendations to fetch
 * @returns {Promise<Array>} Array of recommendations
 */
export const getRecentRecommendations = async (familyCode, limitCount = 5) => {
  try {
    const recommendationsQuery = query(
      collection(db, 'families', familyCode, 'recommendations'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(recommendationsQuery);
    const recommendations = [];

    querySnapshot.forEach(doc => {
      recommendations.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return recommendations;
  } catch (error) {
    logger.error('Error fetching recent recommendations:', error);
    return [];
  }
};

/**
 * Get recent recipes
 * @param {string} familyCode - Family code
 * @param {number} limitCount - Number of recipes to fetch
 * @returns {Promise<Array>} Array of recipes
 */
export const getRecentRecipes = async (familyCode, limitCount = 5) => {
  try {
    const recipesQuery = query(
      collection(db, 'families', familyCode, 'recipes'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(recipesQuery);
    const recipes = [];

    querySnapshot.forEach(doc => {
      recipes.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return recipes;
  } catch (error) {
    logger.error('Error fetching recent recipes:', error);
    return [];
  }
};

/**
 * Get family activity stats
 * @param {string} familyCode - Family code
 * @returns {Promise<Object>} Stats object
 */
export const getFamilyStats = async (familyCode) => {
  try {
    const stats = {
      totalMembers: 0,
      totalAnnouncements: 0,
      totalEvents: 0,
      totalPhotos: 0,
      totalPosts: 0
    };

    // Get member count from NESTED structure: /families/{familyCode}/members
    const membersSnapshot = await getDocs(
      collection(db, 'families', familyCode, 'members')
    );
    stats.totalMembers = membersSnapshot.size;

    // Get announcement count
    const announcementsSnapshot = await getDocs(
      collection(db, 'families', familyCode, 'announcements')
    );
    stats.totalAnnouncements = announcementsSnapshot.size;

    // Get event count
    const eventsSnapshot = await getDocs(
      collection(db, 'families', familyCode, 'events')
    );
    stats.totalEvents = eventsSnapshot.size;

    // Get photo count from familyDocuments (storageType='photos' or legacy images)
    let foundPhotosWithType = false;
    try {
      const photosSnapshot = await getDocs(
        query(
          collection(db, 'families', familyCode, 'familyDocuments'),
          where('storageType', '==', 'photos')
        )
      );
      if (photosSnapshot.size > 0) {
        stats.totalPhotos = photosSnapshot.size;
        foundPhotosWithType = true;
      }
    } catch (indexError) {
      // Index might not exist - fall through to legacy count
      logger.warn('storageType query failed for stats, using fallback');
    }

    if (!foundPhotosWithType) {
      // Fallback: count all image files (legacy data or missing index)
      const allDocsSnapshot = await getDocs(
        collection(db, 'families', familyCode, 'familyDocuments')
      );
      let photoCount = 0;
      allDocsSnapshot.forEach(doc => {
        const data = doc.data();
        const isImage = (data.fileType && data.fileType.startsWith('image/')) ||
          ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(
            (data.fileName || data.name || '').split('.').pop()?.toLowerCase()
          );
        if (isImage) photoCount++;
      });
      stats.totalPhotos = photoCount;
    }

    // Get post count
    const postsSnapshot = await getDocs(
      collection(db, 'families', familyCode, 'posts')
    );
    stats.totalPosts = postsSnapshot.size;

    return stats;
  } catch (error) {
    logger.error('Error fetching family stats:', error);
    return {
      totalMembers: 0,
      totalAnnouncements: 0,
      totalEvents: 0,
      totalPhotos: 0,
      totalPosts: 0
    };
  }
};

/**
 * Get personal photo count for a user
 * Used by Home page to show user's personal photos count
 * @param {string} familyCode - Family code
 * @param {string} userId - User ID
 * @returns {Promise<number>} Personal photo count
 */
export const getPersonalPhotoCount = async (familyCode, userId) => {
  try {
    const personalDocsRef = collection(db, 'families', familyCode, 'members', userId, 'personalDocuments');
    const docsSnapshot = await getDocs(personalDocsRef);

    let photoCount = 0;
    docsSnapshot.forEach(doc => {
      const data = doc.data();
      // Count only image files that aren't deleted
      if (!data.isDeleted) {
        const isImage = (data.fileType && data.fileType.startsWith('image/')) ||
          (data.type === 'file' && ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(
            (data.name || '').split('.').pop()?.toLowerCase()
          ));
        if (isImage) photoCount++;
      }
    });

    return photoCount;
  } catch (error) {
    logger.error('Error fetching personal photo count:', error);
    return 0;
  }
};

/**
 * Get recent discussions
 * @param {string} familyCode - Family code
 * @param {number} limitCount - Number of discussions to fetch
 * @returns {Promise<Array>} Array of discussions
 */
export const getRecentDiscussions = async (familyCode, limitCount = 5) => {
  try {
    const discussionsQuery = query(
      collection(db, 'families', familyCode, 'discussions'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(discussionsQuery);
    const discussions = [];

    querySnapshot.forEach(doc => {
      discussions.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return discussions;
  } catch (error) {
    logger.error('Error fetching recent discussions:', error);
    return [];
  }
};

/**
 * Get recent health updates
 * @param {string} familyCode - Family code
 * @param {number} limitCount - Number of health updates to fetch
 * @returns {Promise<Array>} Array of health updates
 */
export const getRecentHealthUpdates = async (familyCode, limitCount = 5) => {
  try {
    const healthQuery = query(
      collection(db, 'families', familyCode, 'healthUpdates'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(healthQuery);
    const healthUpdates = [];

    querySnapshot.forEach(doc => {
      healthUpdates.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return healthUpdates;
  } catch (error) {
    logger.error('Error fetching recent health updates:', error);
    return [];
  }
};

/**
 * Get family goals
 * @param {string} familyCode - Family code
 * @param {number} limitCount - Number of goals to fetch
 * @returns {Promise<Array>} Array of goals
 */
export const getFamilyGoals = async (familyCode, limitCount = 5) => {
  try {
    const goalsQuery = query(
      collection(db, 'families', familyCode, 'goals'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(goalsQuery);
    const goals = [];

    querySnapshot.forEach(doc => {
      goals.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return goals;
  } catch (error) {
    logger.error('Error fetching family goals:', error);
    return [];
  }
};

/**
 * Get fun & social items
 * @param {string} familyCode - Family code
 * @param {number} limitCount - Number of items to fetch
 * @returns {Promise<Array>} Array of fun/social items
 */
export const getFunSocialItems = async (familyCode, limitCount = 5) => {
  try {
    const funQuery = query(
      collection(db, 'families', familyCode, 'funSocial'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(funQuery);
    const funItems = [];

    querySnapshot.forEach(doc => {
      funItems.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return funItems;
  } catch (error) {
    logger.error('Error fetching fun & social items:', error);
    return [];
  }
};

/**
 * Get upcoming birthdays and anniversaries from family members
 * @param {string} familyCode - Family code
 * @param {number} daysAhead - Number of days to look ahead
 * @returns {Promise<Array>} Array of upcoming birthdays/anniversaries
 */
export const getUpcomingBirthdays = async (familyCode, daysAhead = 30) => {
  try {
    // Get all family members
    const membersSnapshot = await getDocs(
      collection(db, 'families', familyCode, 'members')
    );

    const today = new Date();
    const upcoming = [];

    // Parse a date value into a local (not UTC) Date so month/day are correct.
    // Firestore Timestamps → .toDate() (already local).
    // ISO strings like "1975-12-27" → parse as local to avoid UTC shift.
    const parseLocalDate = (value) => {
      if (!value) return null;
      if (value.toDate) return value.toDate(); // Firestore Timestamp
      if (value instanceof Date) return value;
      const str = String(value);
      // ISO format YYYY-MM-DD — parse as local, not UTC
      const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        return new Date(
          parseInt(isoMatch[1], 10),
          parseInt(isoMatch[2], 10) - 1,
          parseInt(isoMatch[3], 10)
        );
      }
      // Fallback for other formats (e.g. MM/DD/YYYY)
      return new Date(str);
    };

    membersSnapshot.forEach(doc => {
      const member = { id: doc.id, ...doc.data() };

      // Check birthday
      if (member.birthday) {
        const birthdayDate = parseLocalDate(member.birthday);
        if (!birthdayDate) return;
        const birthdayMonth = birthdayDate.getMonth();
        const birthdayDay = birthdayDate.getDate();

        // Calculate next occurrence of birthday
        let nextBirthday = new Date(today.getFullYear(), birthdayMonth, birthdayDay);
        if (nextBirthday < today) {
          nextBirthday = new Date(today.getFullYear() + 1, birthdayMonth, birthdayDay);
        }

        const daysUntil = Math.ceil((nextBirthday - today) / (1000 * 60 * 60 * 24));

        if (daysUntil <= daysAhead) {
          const age = nextBirthday.getFullYear() - birthdayDate.getFullYear();
          upcoming.push({
            id: `birthday-${doc.id}`,
            type: 'birthday',
            name: member.displayName || member.firstName || 'Family Member',
            date: nextBirthday,
            daysUntil,
            age,
            photoUrl: member.photoURL || null,
            isToday: daysUntil === 0
          });
        }
      }

      // Check anniversary
      if (member.anniversaryDate) {
        // Skip if this person's spouse already added this anniversary (to avoid duplicates)
        const existingLinkedAnniversary = upcoming.find(
          u => u.type === 'anniversary' && u.linkedSpouseId === doc.id
        );
        if (existingLinkedAnniversary) {
          return; // Skip - spouse already added this anniversary
        }

        const anniversaryDate = parseLocalDate(member.anniversaryDate);
        if (!anniversaryDate) return;
        const anniversaryMonth = anniversaryDate.getMonth();
        const anniversaryDay = anniversaryDate.getDate();

        let nextAnniversary = new Date(today.getFullYear(), anniversaryMonth, anniversaryDay);
        if (nextAnniversary < today) {
          nextAnniversary = new Date(today.getFullYear() + 1, anniversaryMonth, anniversaryDay);
        }

        const daysUntil = Math.ceil((nextAnniversary - today) / (1000 * 60 * 60 * 24));

        if (daysUntil <= daysAhead) {
          const years = nextAnniversary.getFullYear() - anniversaryDate.getFullYear();

          // Build the display name - if spouse is linked, show "John & Jane"
          let displayName = member.displayName || member.firstName || 'Family Member';
          let linkedSpouseId = null;

          if (member.spouseId && member.spouseName) {
            // Get first name only for cleaner display
            const memberFirstName = (member.displayName || member.firstName || '').split(' ')[0];
            const spouseFirstName = member.spouseName.split(' ')[0];
            displayName = `${memberFirstName} & ${spouseFirstName}`;
            linkedSpouseId = member.spouseId;
          }

          upcoming.push({
            id: `anniversary-${doc.id}`,
            type: 'anniversary',
            name: displayName,
            date: nextAnniversary,
            daysUntil,
            years,
            photoUrl: member.photoURL || null,
            isToday: daysUntil === 0,
            linkedSpouseId // Track the spouse to avoid duplicates
          });
        }
      }
    });

    // Sort by days until
    upcoming.sort((a, b) => a.daysUntil - b.daysUntil);

    return upcoming.slice(0, 10); // Limit to 10 items
  } catch (error) {
    logger.error('Error fetching upcoming birthdays:', error);
    return [];
  }
};

/**
 * Get all dashboard data in one call
 * @param {string} familyCode - Family code
 * @returns {Promise<Object>} Dashboard data object
 */
export const getDashboardData = async (familyCode) => {
  try {
    const [
      announcements,
      events,
      photos,
      recommendations,
      recipes,
      discussions,
      healthUpdates,
      goals,
      funItems,
      birthdays,
      stats
    ] = await Promise.all([
      getRecentAnnouncements(familyCode, 5),
      getUpcomingEvents(familyCode, 5),
      getRecentPhotos(familyCode, 6),
      getRecentRecommendations(familyCode, 5),
      getRecentRecipes(familyCode, 5),
      getRecentDiscussions(familyCode, 5),
      getRecentHealthUpdates(familyCode, 5),
      getFamilyGoals(familyCode, 5),
      getFunSocialItems(familyCode, 5),
      getUpcomingBirthdays(familyCode, 30),
      getFamilyStats(familyCode)
    ]);

    return {
      announcements,
      events,
      photos,
      recommendations,
      recipes,
      discussions,
      healthUpdates,
      goals,
      funItems,
      birthdays,
      stats
    };
  } catch (error) {
    logger.error('Error fetching dashboard data:', error);
    throw error;
  }
};
